<template>
  <div class="page-home">
    <!-- ============ Hero ============ -->
    <section class="hero">
      <div class="hero-grid"></div>
      <div class="hero-glow"></div>
      <div class="hero-content">
        <p class="hero-eyebrow">FANZHIHONG HARDWARE · PRECISION SHEET METAL SAMPLES</p>
        <h1 class="hero-title">专业手钣样品制作</h1>
        <p class="hero-sub">快速响应 · 精准交付 · 全程把控</p>
        <p class="hero-sub-en">FAST RESPONSE &nbsp;·&nbsp; PRECISE DELIVERY &nbsp;·&nbsp; FULL-PROCESS CONTROL</p>
        <div class="hero-buttons">
          <router-link to="/products" class="btn btn-primary">查看产品</router-link>
          <router-link to="/contact" class="btn btn-ghost">联系我们</router-link>
        </div>
      </div>
      <div class="hero-meta">
        <span class="hero-meta-cn">昆山凡志红五金有限公司</span>
        <span class="hero-meta-en">KUNSHAN FANZHIHONG HARDWARE CO., LTD.</span>
      </div>
    </section>

    <!-- ============ 关于我们 ============ -->
    <section class="section about" id="about">
      <div class="container">
        <div class="section-head">
          <span class="section-label">ABOUT US</span>
          <h2 class="section-title">关于<span class="title-accent">凡志红五金</span></h2>
          <p class="section-title-en">About Kunshan Fanzhihong Hardware Co., Ltd.</p>
          <div class="title-divider"></div>
        </div>

        <div class="about-body">
          <div class="about-visual">
            <div class="visual-card" :style="{ backgroundImage: 'url(/images/gallery-07.jpg)' }">
              <div class="visual-overlay"></div>
              <div class="visual-years">
                <span class="years-num">20+</span>
                <span class="years-label">年行业经验</span>
                <span class="years-label-en">YEARS IN THE INDUSTRY</span>
              </div>
            </div>
          </div>

          <div class="about-info">
            <p class="about-lead">
              专注手钣样品定制，提供<span class="text-highlight">快速响应、精准交付、全程把控</span>的专业服务。
              简化下单流程，以专业技术和严谨态度，确保每一件产品都达到高标准。
            </p>
            <p class="about-detail">
              深耕手钣加工领域二十余年，专注<span class="text-highlight">手钣样品制作</span>。
              主营不锈钢、铜材、铝材等多种材质的精密加工。
              从图纸设计到成品交付，严格把控每一道工序，确保产品精度与品质。
            </p>
            <p class="about-detail-en">
              We specialize in custom sheet metal sample fabrication, delivering a professional service built on fast response, precise delivery and full-process control. With a simplified ordering flow, solid expertise and a rigorous attitude, every product we make meets a high standard. Rooted in sheet metal fabrication for over twenty years, we work in stainless steel, copper, aluminium and other materials — controlling every process from drawing design to finished delivery to guarantee precision and quality.
            </p>

            <div class="about-stats">
              <div class="stat-item" v-for="s in stats" :key="s.label">
                <span class="stat-value">{{ s.value }}</span>
                <span class="stat-label">{{ s.label }}</span>
                <span class="stat-label-en">{{ s.labelEn }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 企业资质 ============ -->
    <section class="section qualification">
      <div class="container">
        <div class="section-head">
          <span class="section-label">QUALIFICATION</span>
          <h2 class="section-title">企业资质</h2>
          <p class="section-title-en">Company Qualifications</p>
          <div class="title-divider"></div>
        </div>
        <div class="qual-grid">
          <div class="qual-card" v-for="q in qualifications" :key="q.label">
            <div class="qual-top">
              <span class="qual-icon" v-html="q.icon"></span>
              <span class="qual-label">{{ q.label }}</span>
            </div>
            <span class="qual-value">{{ q.value }}</span>
            <span class="qual-label-en">{{ q.labelEn }}</span>
          </div>
        </div>
        <div class="qual-badges">
          <span class="qual-badge">证照齐全</span>
          <span class="qual-badge">正规注册企业</span>
          <span class="qual-badge">支持来图来样定制</span>
          <span class="qual-badge">可开发票</span>
        </div>
        <p class="qual-note">昆山凡志红五金有限公司 · 统一社会信用代码 91320583MAK34ABY3F</p>
      </div>
    </section>

    <!-- ============ 三大核心优势 ============ -->
    <section class="section strengths">
      <div class="container">
        <div class="section-head">
          <span class="section-label">OUR STRENGTHS</span>
          <h2 class="section-title">三大核心优势</h2>
          <p class="section-title-en">Three Core Strengths</p>
          <div class="title-divider"></div>
        </div>
        <p class="section-desc">从接单到交付，每一个环节都以专业态度对待</p>
        <div class="strength-grid">
          <div class="strength-card" v-for="(s, i) in strengths" :key="s.title">
            <div class="strength-head">
              <span class="strength-icon" v-html="s.icon"></span>
              <span class="strength-num">0{{ i + 1 }}</span>
            </div>
            <h3 class="strength-title">{{ s.title }}</h3>
            <p class="strength-title-en">{{ s.titleEn }}</p>
            <p class="strength-desc">{{ s.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 服务流程 ============ -->
    <section class="section flow">
      <div class="container">
        <div class="section-head">
          <span class="section-label">SERVICE PROCESS</span>
          <h2 class="section-title">服务流程</h2>
          <p class="section-title-en">Simple Ordering Process</p>
          <div class="title-divider"></div>
        </div>
        <p class="section-desc">简化下单流程，四步即可完成样品定制</p>
        <div class="flow-grid">
          <div class="flow-step" v-for="(f, i) in flow" :key="f.title">
            <span class="flow-num">STEP 0{{ i + 1 }}</span>
            <span class="flow-icon" v-html="f.icon"></span>
            <h3 class="flow-title">{{ f.title }}</h3>
            <p class="flow-title-en">{{ f.titleEn }}</p>
            <p class="flow-desc">{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 产品展示（大图） ============ -->
    <section class="section products-preview">
      <div class="container">
        <div class="section-head">
          <span class="section-label">PRODUCTS</span>
          <h2 class="section-title">产品展示</h2>
          <p class="section-title-en">Our Products · Real Shots</p>
          <div class="title-divider"></div>
        </div>
        <div class="showcase-grid">
          <div class="showcase-card" v-for="p in showcase" :key="p.id" @click="$router.push('/products')">
            <div class="showcase-img">
              <img :src="p.imageUrl" :alt="p.name" loading="lazy" />
              <span class="showcase-zoom">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                </svg>
              </span>
            </div>
            <div class="showcase-info">
              <span class="showcase-cat">{{ p.category }}</span>
              <h3 class="showcase-name">{{ p.name }}</h3>
            </div>
          </div>
        </div>
        <div class="section-action">
          <router-link to="/products" class="btn btn-primary">查看全部产品（{{ productTotal }} 种） →</router-link>
        </div>
      </div>
    </section>

    <!-- ============ 样品案例 ============ -->
    <section class="section gallery">
      <div class="container">
        <div class="section-head">
          <span class="section-label">GALLERY</span>
          <h2 class="section-title">样品案例</h2>
          <p class="section-title-en">Sample Gallery · Real Shots of Our Sheet Metal Samples</p>
          <div class="title-divider"></div>
        </div>
        <p class="section-desc">精选手钣样品实拍，涵盖不锈钢、铜材、铝材及各类定制件</p>
        <div class="gallery-grid">
          <div class="gallery-group" v-for="g in galleries" :key="g.title">
            <div class="gallery-tag">GALLERY · {{ g.no }}</div>
            <h3 class="gallery-title">{{ g.title }}</h3>
            <p class="gallery-title-en">{{ g.titleEn }}</p>
            <div class="gallery-images">
              <div class="gallery-item" v-for="img in g.items" :key="img.src">
                <img :src="img.src" :alt="img.caption" loading="lazy" />
                <div class="gallery-caption">
                  <span class="gallery-cat">{{ img.cat }}</span>
                  <span class="gallery-text">{{ img.caption }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 合作企业 ============ -->
    <section class="section partners">
      <div class="container">
        <div class="section-head">
          <span class="section-label">OUR PARTNERS</span>
          <h2 class="section-title">合作企业</h2>
          <p class="section-title-en">Cooperative Partners</p>
          <div class="title-divider"></div>
        </div>
        <p class="section-desc">与多家行业企业保持稳定合作，共同为客户提供可靠的产品与服务</p>
        <div class="partner-grid">
          <div class="partner-card" v-for="p in partners" :key="p.name">
            <span class="partner-name">{{ p.name }}</span>
            <span class="partner-name-en">{{ p.nameEn }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ CTA ============ -->
    <section class="section cta">
      <div class="container">
        <h2>需要手钣样品制作服务？</h2>
        <p class="cta-en">Need sheet metal sample fabrication services?</p>
        <p>欢迎随时联系我们，我们将为您提供专业的解决方案</p>
        <router-link to="/contact" class="btn btn-primary">立即咨询</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { getProducts } from '../api/portal'

export default {
  name: 'Home',
  data() {
    return {
      products: [],
      stats: [
        { value: '20+', label: '年行业深耕', labelEn: 'Years in the Industry' },
        { value: '500+', label: '服务客户数', labelEn: 'Clients Served' },
        { value: '50+', label: '材料品类', labelEn: 'Material Categories' },
        { value: '100%', label: '客户满意度', labelEn: 'Client Satisfaction' }
      ],
      qualifications: [
        {
          label: '统一社会信用代码', value: '91320583MAK34ABY3F', labelEn: 'Unified Social Credit Code',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>'
        },
        {
          label: '法定代表人', value: '秦志成', labelEn: 'Legal Representative',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
        },
        {
          label: '注册资本', value: '100万元整', labelEn: 'Registered Capital',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="M8.5 8l3.5 4 3.5-4M12 12v5M9.5 13.5h5M9.5 15.5h5"/></svg>'
        },
        {
          label: '成立日期', value: '2025年12月08日', labelEn: 'Date of Establishment',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>'
        },
        {
          label: '企业类型', value: '有限责任公司', labelEn: 'Company Type',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1"/></svg>'
        },
        {
          label: '注册地址', value: '江苏省昆山市玉山镇富士康路758-5号', labelEn: 'Registered Address',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>'
        }
      ],
      strengths: [
        {
          title: '快速响应', titleEn: 'FAST RESPONSE',
          desc: '需求对接高效，打样周期短，快速出方案、出样品，让您的产品尽快落地。',
          icon: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M13 2L3 14h8l-1 8 11-13h-8z"/></svg>'
        },
        {
          title: '精准交付', titleEn: 'PRECISE DELIVERY',
          desc: '按图加工，公差控制严格，按期交付，保证每一件样品都符合图纸要求。',
          icon: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/></svg>'
        },
        {
          title: '全程把控', titleEn: 'FULL-PROCESS CONTROL',
          desc: '从图纸设计到成品交付，每一道工序严格检验，确保产品精度与品质稳定。',
          icon: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z"/><path d="M9 11.5l2 2 4-4.5"/></svg>'
        }
      ],
      flow: [
        {
          title: '图纸沟通', titleEn: 'DRAWINGS & REQUIREMENTS',
          desc: '来图来样，快速评估工艺可行性与材质方案。',
          icon: '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 11.5a8.38 8.38 0 01-8.5 8.5 8.5 8.5 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 018.5-8.5 8.38 8.38 0 018.5 8.5z"/></svg>'
        },
        {
          title: '方案报价', titleEn: 'SOLUTION & QUOTATION',
          desc: '确定材质、工艺与周期，透明快速报价。',
          icon: '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 7h8M8 11h8M8 15h4"/></svg>'
        },
        {
          title: '打样制作', titleEn: 'SAMPLING & FABRICATION',
          desc: '精密冲压折弯加工，进度实时可控。',
          icon: '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="3.2"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>'
        },
        {
          title: '检验交付', titleEn: 'INSPECTION & DELIVERY',
          desc: '全检合格后安全包装，按期发货交付。',
          icon: '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/></svg>'
        }
      ],
      categories: [
        { name: '不锈钢样品', nameEn: 'STAINLESS STEEL', desc: '各类不锈钢材质手钣样品制作' },
        { name: '铜材样品', nameEn: 'COPPER', desc: '铜材手钣加工，精度高，品质好' },
        { name: '铝材样品', nameEn: 'ALUMINIUM', desc: '铝合金材质手钣定制加工' },
        { name: '定制样品', nameEn: 'CUSTOM', desc: '来图来样，按需定制各类手钣' }
      ],
      galleries: [
        {
          no: '01', title: '精密铜件与导电端子', titleEn: 'Precision Copper Parts & Conductive Terminals',
          items: [
            { src: '/images/gallery-01.jpg', cat: '铜材样品', caption: '连接器插针 / 端子（镀金）' },
            { src: '/images/gallery-02.jpg', cat: '铜材样品', caption: '紫铜导电弹片 / 铜排支架' }
          ]
        },
        {
          no: '02', title: '弹性接触件与电池弹片', titleEn: 'Elastic Contacts & Battery Springs',
          items: [
            { src: '/images/gallery-03.jpg', cat: '铜材样品', caption: '多联式精密弹片模组' },
            { src: '/images/gallery-04.jpg', cat: '铜材样品', caption: '小型连接器母端弹片（镀金）' }
          ]
        },
        {
          no: '03', title: '环形接触件与弹性套筒', titleEn: 'Ring Contacts & Elastic Sleeves',
          items: [
            { src: '/images/gallery-05.jpg', cat: '定制样品', caption: '环形电池弹片 / 法兰接触环' },
            { src: '/images/gallery-06.jpg', cat: '定制样品', caption: '弹性卡箍 / 套筒结构' }
          ]
        },
        {
          no: '04', title: '连续模半成品与开槽铜套', titleEn: 'Progressive-Die Parts & Slotted Copper Sleeves',
          items: [
            { src: '/images/gallery-08.jpg', cat: '不锈钢样品', caption: '料带式冲压半成品（连续模生产）' },
            { src: '/images/gallery-11.jpg', cat: '铜材样品', caption: '开槽铜套筒（集流环 / 弹性接触）' }
          ]
        },
        {
          no: '05', title: '精密弹片与接触组件', titleEn: 'Precision Springs & Contact Assemblies',
          items: [
            { src: '/images/gallery-09.jpg', cat: '铜材样品', caption: '精密弹片组件' },
            { src: '/images/gallery-12.jpg', cat: '不锈钢样品', caption: '冲压接触零件' }
          ]
        },
        {
          no: '06', title: '定制结构件与工装', titleEn: 'Custom Structural Parts & Tooling',
          items: [
            { src: '/images/gallery-10.jpg', cat: '定制样品', caption: '定制钣金结构件' },
            { src: '/images/gallery-07.jpg', cat: '定制样品', caption: '样品检测与装配' }
          ]
        }
      ],
      partners: [
        { name: '启基科技股份有限公司', nameEn: 'Qiji Technology Co., Ltd.' },
        { name: '祥龙嘉业电子科技有限公司', nameEn: 'Xianglong Jiaye Electronic Technology Co., Ltd.' },
        { name: '安费诺', nameEn: 'Amphenol Corporation' },
        { name: '泰科电子', nameEn: 'Tyco Electronics Technology Co., Ltd.' },
        { name: '贝尔威勒电子（昆山）', nameEn: 'Beierweile Electronics (Kunshan) Co., Ltd.' }
      ]
    }
  },
  computed: {
    productTotal() {
      return this.products.length
    },
    showcase() {
      // 每个品类取前 2 个产品，共 8 个大图展示
      const picked = []
      const cats = ['不锈钢样品', '铜材样品', '铝材样品', '定制样品']
      cats.forEach(c => {
        picked.push(...this.products.filter(p => p.category === c).slice(0, 2))
      })
      // 若某品类缺产品，用其余产品补足到 8 个
      if (picked.length < 8) {
        this.products.forEach(p => {
          if (picked.length < 8 && !picked.includes(p)) picked.push(p)
        })
      }
      return picked
    }
  },
  async created() {
    try {
      const res = await getProducts()
      if (res.data && res.data.success) {
        this.products = res.data.data || []
      }
    } catch (e) {
      /* 首页产品展示加载失败不影响页面 */
    }
  }
}
</script>
